import { Auth, API, Hub } from 'aws-amplify';

const memos = {};

Hub.listen('auth', data => {
	switch (data?.payload?.event) {
		case 'signIn':
		case 'autoSignIn':
		case 'signOut':
			for (const k of Object.keys(memos)) {
				delete memos[k];
			}
			break;
		default:
			break;
	}
});

export default class ProApi {
	async _getFeatures() {
		try {
			// TODO: Remember, when we migrate to v6, checking the current
			// user no longer raises an exception when signed out.
			const user = await Auth.currentAuthenticatedUser();
			const features = await API.get(
				'mtgdeckanalyzerapiv1',
				`/v1/subs/features/${user.attributes.sub}`
			);
			return features;
		} catch {
			return {};
		}
	};

	/**
	 * Retrieves the record of features the user has access to as a record of
	 * feature name to boolean (true if enabled).
	 * 
	 * Notablye: Calls are memoized.
	 * 
	 * @returns {Promise<Record<string, boolean>>}
	 */
	getFeatures() {
		const k = 'getFeatures';
		if (!memos[k]) {
			memos[k] = this._getFeatures();
		}
		return memos[k];
	};

	/**
	 * 
	 * @returns {Promise<string>}
	 */
	async getUserId() {
		try {
			const user = await Auth.currentAuthenticatedUser();
			return user.attributes.sub;
		} catch {
			return null;
		}
	};

	/**
	 * @param {string} priceId
	 * @returns {string | null}
	 */
	async getUpgradeLink(priceId) {
		try {
			// TODO: Remember, when we migrate to v6, checking the current
			// user no longer raises an exception when signed out.
			const user = await Auth.currentAuthenticatedUser();
			const returnUrl = encodeURIComponent(document.location);
			const url = await API.get(
				'mtgdeckanalyzerapiv1',
				`/v1/subs/start-checkout/${user.attributes.sub}?return=${returnUrl}&priceId=${priceId}`
			);
			return url;
		} catch {
			return null;
		}
	};

	/**
	 * @typedef {{
	 * 	id: string;
	 * 	type: 'one_time' | 'recurring';
	 * 	interval?: 'month' | 'year';
	 * 	price: number;
	 * }} Price
	 */
	
	/**
	 * @returns {Promise<{
	 * 	id: string;
	 * 	name: string;
	 * 	description: string;
	 * 	features: string[];
	 * 	marketing_features: {name: string}[];
	 * 	prices: Price[]
	 * }[]>}
	 */
	async getUpgradeOptions() {
		let user;
		try {
			// TODO: Remember, when we migrate to v6, checking the current
			// user no longer raises an exception when signed out.
			user = await Auth.currentAuthenticatedUser();
		} catch {
			user = { attributes: { sub: 'unauthenticated' } };
		}

		const options = await API.get(
			'mtgdeckanalyzerapiv1',
			`/v1/subs/list-upgrade-options/${user.attributes.sub}`
		);
		return options;
	};
	
	async getSubscriptionManagementLink() {
		try {
			// TODO: Remember, when we migrate to v6, checking the current
			// user no longer raises an exception when signed out.
			const user = await Auth.currentAuthenticatedUser();
			const returnUrl = encodeURIComponent(document.location);
			const subs = await API.get(
				'mtgdeckanalyzerapiv1',
				`/v1/subs/manage-subscription-url/${user.attributes.sub}?return=${returnUrl}`
			);
			return subs;
		} catch {
			return null;
		}
	};

	async getActiveSubscription() {
		try {
			// TODO: Remember, when we migrate to v6, checking the current
			// user no longer raises an exception when signed out.
			const user = await Auth.currentAuthenticatedUser();
			if (!user) return [];

			const subs = await API.get(
				'mtgdeckanalyzerapiv1',
				`/v1/subs/list-user-subscriptions/${user.attributes.sub}`
			);
			return subs;
		} catch {
			return [];
		}
	};

	async cancelSubscription(subId) {
		try {
			// TODO: Remember, when we migrate to v6, checking the current
			// user no longer raises an exception when signed out.
			const user = await Auth.currentAuthenticatedUser();
			const result = await API.get(
				'mtgdeckanalyzerapiv1',
				`/v1/subs/cancel-user-subscription/${user.attributes.sub}?subId=${subId}`
			);
			return result === 'ok';
		} catch {
			return null;
		}
	};

	getLLMAnalysis(data) {
		const k = JSON.stringify(['getLLMAnalysis', data]);
		if (!memos[k]) {
			memos[k] = this._getLLMAnalysis(data);
		}
		return memos[k];
	};

	async _getLLMAnalysis(data) {
		try {
			// TODO: Remember, when we migrate to v6, checking the current
			// user no longer raises an exception when signed out.
			const user = await Auth.currentAuthenticatedUser();
			const result = await API.post(
				'mtgdeckanalyzerapiv1',
				`/v1/analyze/llm`, {
					body: {
						data
					}
				});
			return result;
		} catch {
			return null;
		}
	};
}
